import { Suspense } from 'react';
import { Spinner, Stack } from '@customerio/pluma-components/react';
import { channelValueOf } from '@/features/deliveries';
import type { Delivery } from '@/services/db/delivery/entity';
import { useDelivery, useDeliveryDetailContent } from '@/services/db/delivery/queries';
import { DeliveryDetailAlert } from './components/delivery-detail-alert';
import { DeliveryDetailMetadata } from './components/delivery-detail-metadata';
import { DeliveryJourneysPanel } from './components/delivery-journeys-panel';
import { DeliveryMessagePreview } from './components/delivery-message-preview';
import { DeliveryMetricsTimeline } from './components/delivery-metrics-timeline';
import { DeliveryPreviewHeader } from './components/delivery-preview-header';
import { InAppConfig } from './components/in-app-config';
import { InboxConfig } from './components/inbox-config';
import { RelatedDeliveries } from './components/related-deliveries';
import { WebhookResponseBlock } from './components/webhook-response-block';

type DeliveryDetailPageProps = {
  /** Delivery id from the route (`/journeys/outbox/deliveries/:deliveryId`). */
  deliveryId: string;
  /** Builds a related delivery's href — drafts route to Ember, sent to the React detail. */
  getDeliveryHref: (delivery: Delivery) => string;
};

/**
 * Delivery detail: metadata (campaign / broadcast / action / newsletter /
 * transactional related-model links), the in-app / inbox config panels, the
 * related-deliveries (resend-chain) panel, and the rendered message, from the
 * existing `services/db` data layer. Still deferred: draft edit mode; route
 * wiring lands with the Outbox page (FPS-2591).
 */
export function DeliveryDetailPage({ deliveryId, getDeliveryHref }: DeliveryDetailPageProps) {
  return (
    <Suspense fallback={<Spinner />}>
      <DeliveryDetailContent deliveryId={deliveryId} getDeliveryHref={getDeliveryHref} />
    </Suspense>
  );
}

type DeliveryDetailContentProps = { deliveryId: string; getDeliveryHref: (delivery: Delivery) => string };

function DeliveryDetailContent({ deliveryId, getDeliveryHref }: DeliveryDetailContentProps) {
  const { data: delivery } = useDelivery({ id: deliveryId });

  return (
    <Stack gap="300">
      <DeliveryDetailMetadata delivery={delivery} />
      {/* Metadata, metrics, and the Journeys panel all derive purely from the
          delivery, so they render outside the detail-content Suspense — a
          message-body fetch failure must not tear them down (the expanded-row
          path decouples the same way; see services/db/delivery/queries.ts). */}
      <DeliveryMetricsTimeline delivery={delivery} />
      <DeliveryJourneysPanel delivery={delivery} />
      <Suspense fallback={<Spinner />}>
        <DeliveryPreview deliveryId={deliveryId} delivery={delivery} getDeliveryHref={getDeliveryHref} />
      </Suspense>
    </Stack>
  );
}

type DeliveryPreviewProps = {
  deliveryId: string;
  delivery: Delivery;
  getDeliveryHref: (delivery: Delivery) => string;
};

/**
 * The channel config panel (in-app / inbox only) + preview header + status
 * alert + related-deliveries (resend-chain) panel + rendered message; all read
 * the same suspended detail content. Ember renders the config panels in the
 * right-hand aside next to the metadata; the Hydra shell (FPS-2592) ships a
 * single column, so they render here in the main flow after the metadata,
 * metrics, and Journeys panels. The related-deliveries panel sits between the
 * header and the message (as in Ember); the webhook response follows the
 * message, in the preview area.
 */
function DeliveryPreview({ deliveryId, delivery, getDeliveryHref }: DeliveryPreviewProps) {
  const { data: detail } = useDeliveryDetailContent({ id: deliveryId });
  const channel = channelValueOf(delivery.action_type);

  return (
    <>
      {channel === 'in_app' ? <InAppConfig detail={detail} /> : null}
      {channel === 'inbox' ? <InboxConfig detail={detail} /> : null}
      <DeliveryPreviewHeader delivery={delivery} detail={detail} />
      <DeliveryDetailAlert delivery={delivery} detail={detail} />
      <RelatedDeliveries delivery={delivery} getDeliveryHref={getDeliveryHref} />
      <DeliveryMessagePreview deliveryId={deliveryId} delivery={delivery} />
      <WebhookResponseBlock delivery={delivery} />
    </>
  );
}
